/** Background CSV imports (Shopify customers / orders export) + progress of all import jobs, including product image copies. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Progress, Space, Table, Tag, Typography, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { directUpload } from './api';
import { fmtDate, listOf, toastErr, useSite } from './common';

const { Text } = Typography;
const KINDS = { product_images: 'Product images', customers: 'Customers', orders: 'Order history' };
const COLORS = { queued: 'default', running: 'processing', done: 'success', failed: 'error' };

const ShopImports = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [jobs, setJobs] = useState([]);
    const [busy, setBusy] = useState('');
    const timer = useRef(null);
    const load = useCallback(() => api.get('/imports').then((d) => { setJobs(listOf(d)); return listOf(d); }), [api]);
    useEffect(() => {
        let live = true;
        const tick = () => load().catch(() => []).then((l) => {
            if (live) timer.current = setTimeout(tick, l.some((j) => ['queued', 'running'].includes(j.status)) ? 3000 : 20000);
        });
        tick();
        return () => { live = false; clearTimeout(timer.current); };
    }, [load]);
    const start = async (kind, file) => {
        setBusy(kind);
        try {
            // the API takes no file uploads: store the CSV directly, then hand over its key
            const up = await directUpload(api, file, 'import');
            await api.post(`/imports/${kind}`, { key: up.key });
            message.success(t('Import started'));
            await load();
        } catch (e) { toastErr(e); } finally { setBusy(''); }
        return false;
    };
    const picker = (kind, label) => (
        <Upload accept=".csv" showUploadList={false} beforeUpload={(f) => { start(kind, f); return false; }}>
            <Button icon={<UploadOutlined />} loading={busy === kind}>{t(label)}</Button>
        </Upload>
    );
    return (
        <Card size="small" title={t('Import')}>
            <Space wrap style={{ marginBottom: 12 }}>
                {picker('customers', 'Import customers (Shopify CSV)')}
                {picker('orders', 'Import order history (Shopify CSV)')}
            </Space>
            <Table size="small" rowKey="id" pagination={false} dataSource={jobs} scroll={{ x: 640 }} columns={[
                { title: t('Type'), dataIndex: 'kind', render: (v) => t(KINDS[v] || v) },
                { title: t('Status'), dataIndex: 'status', render: (v, r) => <><Tag color={COLORS[v]}>{t({ queued: 'Queued', running: 'Running', done: 'Done', failed: 'Failed' }[v] || v)}</Tag>{r.error && <Text type="danger">{r.error}</Text>}</> },
                { title: t('Progress'), key: 'p', width: 170, render: (_, r) => <Progress percent={r.progress} size="small" status={r.status === 'failed' ? 'exception' : undefined} /> },
                { title: t('Failed'), dataIndex: 'failed', width: 70 },
                { title: t('Created'), dataIndex: 'created_at', render: (v) => fmtDate(v) },
            ]} expandable={{ rowExpandable: (r) => (r.errors || []).length > 0,
                expandedRowRender: (r) => r.errors.map((e, i) => <div key={i}><Text type="secondary">{e.ref}</Text> {e.error}</div>) }} />
        </Card>
    );
};

export default ShopImports;
